import { flatMap, uniqBy } from 'es-toolkit';
import i18n from 'i18next';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { getCarriersPayload, getCollaboratorsPayload, getErrorMessageFromAPI, valueToNumber } from '../../utilz';
import { getExtendedCarrierAndBidDataEffect, getFilteredGuidesEffect } from '../RoutingGuide/RoutingGuideEffects';
import {
  createSpotQuoteInError,
  createSpotQuoteInSuccess,
  getCarrierDetailsInError,
  getCarrierDetailsInSuccess,
  getCarrierSearchError,
  getCarrierSearchSuccess,
  getNewSpotQuoteNoError,
  getNewSpotQuoteNoSuccess,
  getPortSearchError,
  getPortSearchSuccess,
  getPostalSearchError,
  getPostalSearchSuccess,
  getSpotQuoteTransportationInError,
  getSpotQuoteTransportationInSuccess,
  setAppLoader,
  setCurrentQuoteCarriers,
  setCurrentQuoteCollaborators,
} from './CommonActions';
import {
  createSpotQuoteEffect,
  getCarrierDetailsEffect,
  getCarrierSearchWithPaginationEffect,
  getNewSpotQuoteNoEffect,
  getPostalSearch,
  getShipmentPortList,
  getSpotQuoteTransportationModesEffect,
  putSpotQuoteBidders,
  putSpotQuoteCollaborator,
  uploadQuoteDocumentEffect,
} from './CommonEffects';
import CommonTypes from './CommonTypes';

function* handlePostalSearch({ payload }) {
  try {
    const { data } = yield call(getPostalSearch, payload);
    yield put(getPostalSearchSuccess(data));
  } catch (error) {
    const message = getErrorMessageFromAPI(error, i18n.t('translation:default.notification.postalDataFailed'));
    yield put(getPostalSearchError({ message }));
  }
}

function* handlePortSearch({ payload }) {
  try {
    const { data } = yield call(getShipmentPortList, payload);
    yield put(getPortSearchSuccess(data));
  } catch (error) {
    const message = getErrorMessageFromAPI(error, i18n.t('translation:default.notification.portDataFailed'));
    yield put(getPortSearchError({ message }));
  }
}

function* handleCarrierSearch({ payload }) {
  try {
    const { data } = yield call(getCarrierSearchWithPaginationEffect, payload);
    yield put(
      getCarrierSearchSuccess({
        data: data?.data ? data?.data : [],
        totalRecords: valueToNumber(data?.totalRecords),
      }),
    );
  } catch (error) {
    const message = getErrorMessageFromAPI(error, i18n.t('translation:default.notification.carrierDataFailed'));
    yield put(getCarrierSearchError({ message }));
  }
}

function* getCarrierDetailsWatcher({ payload }) {
  try {
    const { data } = yield call(getCarrierDetailsEffect, payload);
    yield put(
      getCarrierDetailsInSuccess({
        data: data ? data : [],
      }),
    );
  } catch (e) {
    yield put(
      getCarrierDetailsInError({
        message: getErrorMessageFromAPI(e, 'Failed to get carrier details'),
      }),
    );
  }
}

function* getNewSpotQuoteNo() {
  try {
    const { data } = yield call(getNewSpotQuoteNoEffect);
    yield put(getNewSpotQuoteNoSuccess(data));
  } catch (error) {
    yield put(getNewSpotQuoteNoError({ message: getErrorMessageFromAPI(error) }));
  }
}

function* getSpotQuoteTransportationModes() {
  try {
    const { data } = yield call(getSpotQuoteTransportationModesEffect);
    yield put(getSpotQuoteTransportationInSuccess(data));
  } catch (_error) {
    yield put(getSpotQuoteTransportationInError([]));
  }
}

function* callCrateSpotQuote({ payload }) {
  yield put(setAppLoader(true));

  const { data } = yield call(createSpotQuoteEffect, payload);

  if (data === 0) {
    throw i18n.t('translation:default.notification.errorWithCode0');
  }

  yield put(createSpotQuoteInSuccess({ data }));

  return data;
}

function* putQuoteCarriersAndCollaborators({ carriers, collaborators, newQuoteId, isCollaboratorsEnabled }) {
  const carrierPayload = {
    carriers: getCarriersPayload(carriers),
    quoteKey: newQuoteId,
  };

  const collaboratorPayload = {
    collaborators: getCollaboratorsPayload(collaborators),
    spotQuoteId: newQuoteId,
  };

  const requests = [];

  if (
    Array.isArray(collaboratorPayload.collaborators) &&
    collaboratorPayload.collaborators.length > 0 &&
    isCollaboratorsEnabled
  ) {
    requests.push(call(putSpotQuoteCollaborator, collaboratorPayload));
  }

  if (Array.isArray(carrierPayload.carriers) && carrierPayload.carriers.length > 0) {
    requests.push(call(putSpotQuoteBidders, carrierPayload));
  }

  yield all(requests);
}

function* putQuoteDocuments({ quoteDocuments, quoteKey }) {
  for (let i = 0; i < quoteDocuments.length; i++) {
    quoteDocuments[i].append('QuoteKey', quoteKey);

    yield call(uploadQuoteDocumentEffect, quoteDocuments[i]);
  }
}

function* createSpotQuote({
  payload: { handleSuccess, isPartialSave, quoteDocuments, isCollaboratorsEnabled, ...payload },
}) {
  try {
    const collaborators = yield select((state) => state.common.currentSpotCollaborators);
    const carriers = yield select((state) => state.common.currentSpotCarriers);

    const newQuoteId = yield call(callCrateSpotQuote, { payload });

    if (newQuoteId) {
      yield call(putQuoteCarriersAndCollaborators, { carriers, collaborators, isCollaboratorsEnabled, newQuoteId });

      yield call(putQuoteDocuments, { quoteDocuments, quoteKey: newQuoteId });

      yield call(showNotification, {
        message: 'Quote created',
        type: NOTIFICATION_TYPES.SUCCESS,
      });
    }
  } catch (e) {
    const errorMessage = getErrorMessageFromAPI(e);

    yield call(showNotification, {
      message: `${errorMessage}`,
      type: NOTIFICATION_TYPES.ERROR,
    });

    yield put(createSpotQuoteInError({ message: errorMessage }));
  } finally {
    yield put(setAppLoader(false));
  }
}

function* getCurrentQuoteCollaborators() {
  const mapUniqEntities = (collection, propName, iteratee) => {
    return uniqBy(
      flatMap(collection, (item) => item[propName] || []),
      (item) => item[iteratee],
    );
  };

  const normalizeCollaborators = (collaborators) =>
    collaborators.map((value) => ({
      emails: value.email,
      group: value.group,
      notificationsEnabled: value.notificationsEnabled,
      role: value.role,
      userId: value.userId,
      userName: value.name,
    }));

  const filterParams = yield select((state) => state.common.currentSpotRoutingGuidesParams);
  const copyQuoteKey = yield select((state) => state.common.copyQuoteKey);

  try {
    const { data } = yield call(getFilteredGuidesEffect, filterParams);
    if (data.routingGuides) {
      const collaborators = normalizeCollaborators(mapUniqEntities(data.routingGuides, 'collaborators', 'userId'));
      const routingCarriers = mapUniqEntities(data.routingGuides, 'carriers', 'carrierId');
      if (!copyQuoteKey) {
        yield put(setCurrentQuoteCollaborators(collaborators));
      }

      let { data: carriers } = yield call(getExtendedCarrierAndBidDataEffect, filterParams);
      carriers = carriers.map((carrier) => {
        const routing = routingCarriers.find((item) => item.carrierId === carrier.carrierKey);
        if (routing) {
          return {
            ...carrier,
            carrierName: routing.transportationProvider,
            email: routing.email,
          };
        }
        return carrier;
      });
      yield put(setCurrentQuoteCarriers(carriers));
    }
  } catch (_e) {
    yield put(setAppLoader(false));
  }
}

function* getCurrentQuoteCarriers() {
  const filterParams = yield select((state) => state.common.currentSpotRoutingGuidesParams);
  try {
    const { data } = yield call(getExtendedCarrierAndBidDataEffect, filterParams);
    yield put(setCurrentQuoteCarriers(data));
  } catch (_e) {
    yield put(setAppLoader(false));
  }
}

export default function* commonSaga() {
  yield takeEvery(CommonTypes.GET_POSTAL_SEARCH_PROGRESS, handlePostalSearch);
  yield takeEvery(CommonTypes.GET_PORT_SEARCH_PROGRESS, handlePortSearch);
  yield takeEvery(CommonTypes.GET_CARRIER_SEARCH_PROGRESS, handleCarrierSearch);
  yield takeEvery(CommonTypes.GET_CARRIER_DETAILS_PROGRESS, getCarrierDetailsWatcher);
  yield takeEvery(CommonTypes.GET_NEW_SPOT_QUOTE_NO_IN_PROGRESS, getNewSpotQuoteNo);
  yield takeLatest(CommonTypes.CREATE_SPOT_QUOTE_PROGRESS, createSpotQuote);
  yield takeLatest(CommonTypes.GET_CURRENT_QUOTE_CARRIERS_LIST, getCurrentQuoteCarriers);
  yield takeLatest(CommonTypes.GET_CURRENT_QUOTE_COLLABORATORS_LIST, getCurrentQuoteCollaborators);
  yield takeLatest(CommonTypes.GET_SPOT_QUOTE_TRANSPORTATION_MODES_IN_PROGRESS, getSpotQuoteTransportationModes);
}
